import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatErr } from '@/lib/api';
import { PageHeader, PageShell } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Video, Phone, Building, ExternalLink } from 'lucide-react';
import RangeChips from '@/components/RangeChips';
import { useAuth } from '@/context/AuthContext';

const MODE_ICON = { telephonic: Phone, video: Video, f2f: Building };

export default function Interviews() {
  const { user } = useAuth();
  const canFilter = ['admin', 'manager'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [cands, setCands] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ candidate_id: '', job_id: '', scheduled_at: '', mode: 'video', round_name: 'Round 1', interviewer: '', status: 'scheduled', feedback: '' });
  const [rangeKey, setRangeKey] = useState('all');
  const [range, setRange] = useState(null);
  const [byFilter, setByFilter] = useState('all');

  const load = async () => {
    try {
      const params = {};
      if (range) { params.from_date = range.from; params.to_date = range.to; }
      if (canFilter && byFilter !== 'all') params.scheduled_by = byFilter;
      const { data } = await api.get('/interviews', { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rangeKey, range, byFilter]);
  useEffect(() => {
    api.get('/candidates').then(({ data }) => setCands(data)).catch(() => {});
    api.get('/jobs').then(({ data }) => setJobs(data)).catch(() => {});
    if (canFilter) api.get('/users').then(({ data }) => setUsers(data)).catch(() => {});
  }, [canFilter]);

  const save = async () => {
    if (!form.candidate_id || !form.scheduled_at) { toast.error('Candidate and time required'); return; }
    try {
      await api.post('/interviews', { ...form, job_id: form.job_id || null, scheduled_at: new Date(form.scheduled_at).toISOString() });
      toast.success('Interview scheduled'); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const upcoming = rows.filter(r => new Date(r.scheduled_at) >= new Date() && r.status === 'scheduled');
  const past = rows.filter(r => !(new Date(r.scheduled_at) >= new Date() && r.status === 'scheduled'));

  const candName = (id) => cands.find(c => c.id === id)?.name || '—';
  const jobName = (id) => { const j = jobs.find(j => j.id === id); return j ? `${j.title} · ${j.client}` : '—'; };

  const Card = ({ r }) => {
    const Icon = MODE_ICON[r.mode] || Video;
    const dt = new Date(r.scheduled_at);
    return (
      <div className="tactical-card p-4 hover:-translate-y-0.5 transition-transform">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.round_name}</div>
            <Link to={`/lineups/${r.candidate_id}`} data-testid={`int-cand-link-${r.id}`} className="group inline-flex items-center gap-1 hover:text-primary transition-colors">
              <span className="font-display text-lg font-black tracking-tight truncate">{candName(r.candidate_id)}</span>
              <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
            <div className="text-xs text-muted-foreground mt-0.5">{jobName(r.job_id)}</div>
          </div>
          <Badge variant="outline" className="capitalize flex-shrink-0"><Icon className="w-3 h-3 mr-1" />{r.mode}</Badge>
        </div>
        <div className="mt-3 pt-3 border-t border-border/70 flex items-center justify-between text-xs">
          <div className="font-display font-black text-foreground">{dt.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
          <div className="text-muted-foreground">{r.interviewer || 'TBD'}</div>
        </div>
      </div>
    );
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Calendar"
        title="Interviews"
        subtitle="Schedule and track every candidate interview across your pipeline."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-interview-btn" className="bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" />Schedule</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl font-black">Schedule interview</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Candidate <span className="text-xs text-muted-foreground font-normal">· pick from Lineups</span></Label>
                  <Select value={form.candidate_id} onValueChange={(v) => setForm({ ...form, candidate_id: v })}>
                    <SelectTrigger data-testid="int-cand"><SelectValue placeholder="Choose from Lineups" /></SelectTrigger>
                    <SelectContent>{cands.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.current_company || '—'}</SelectItem>)}</SelectContent>
                  </Select>
                  {cands.length === 0 && <div className="text-[11px] text-amber-600 mt-1">No lineups yet — add one from the Lineups tab first.</div>}
                </div>
                <div>
                  <Label>Job</Label>
                  <Select value={form.job_id || 'none'} onValueChange={(v) => setForm({ ...form, job_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title} · {j.client}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date & time</Label><Input data-testid="int-time" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></div>
                  <div>
                    <Label>Mode</Label>
                    <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="video">Video</SelectItem><SelectItem value="telephonic">Telephonic</SelectItem><SelectItem value="f2f">Face to face</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Round</Label><Input value={form.round_name} onChange={(e) => setForm({ ...form, round_name: e.target.value })} /></div>
                  <div><Label>Interviewer</Label><Input value={form.interviewer} onChange={(e) => setForm({ ...form, interviewer: e.target.value })} placeholder="Name" /></div>
                </div>
                <Button data-testid="int-save" onClick={save} className="w-full bg-primary hover:bg-primary/90 text-white">Schedule</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="tactical-card p-3 mb-4 flex flex-wrap items-center gap-3">
        <RangeChips value={rangeKey} onChange={(k, r) => { setRangeKey(k); setRange(r); }} testidPrefix="int-range" />
        {canFilter && (
          <div className="ml-auto flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-widest">Scheduled by</Label>
            <Select value={byFilter} onValueChange={setByFilter}>
              <SelectTrigger className="w-56 h-9" data-testid="int-by-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All recruiters</SelectItem>
                {users.filter(u => u.role !== 'admin').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {canFilter && rows.length > 0 && (
        <div className="tactical-card p-4 mb-4">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">By recruiter · {rows.length} total</div>
          <div className="flex flex-wrap gap-2" data-testid="int-by-counts">
            {Object.entries(rows.reduce((m, r) => { m[r.scheduled_by_name || '—'] = (m[r.scheduled_by_name || '—'] || 0) + 1; return m; }, {}))
              .sort((a, b) => b[1] - a[1])
              .map(([name, count]) => (
                <Badge key={name} variant="outline" className="text-xs py-1">
                  <span className="font-medium">{name}</span>
                  <span className="ml-2 font-display font-black text-primary">{count}</span>
                </Badge>
              ))}
          </div>
        </div>
      )}

      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Upcoming · {upcoming.length}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="int-upcoming">
          {upcoming.map(r => <Card key={r.id} r={r} />)}
          {upcoming.length === 0 && <div className="col-span-full text-center p-10 tactical-card text-muted-foreground">No upcoming interviews.</div>}
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Past / other · {past.length}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {past.map(r => <Card key={r.id} r={r} />)}
          {past.length === 0 && <div className="col-span-full text-center p-6 text-sm text-muted-foreground">Nothing here yet.</div>}
        </div>
      </div>
    </PageShell>
  );
}
